import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import WorkspaceNav from "./WorkspaceNav";
import WorkspaceIcon from "./WorkspaceIcon";
import {
  buildBreadcrumbs,
  getWorkspace,
  startWorkspaceHandoff,
  writeStoredWorkspace,
} from "./workspaceRegistry";
import { useActiveWorkspace } from "./ActiveWorkspaceContext";
import "./WorkspaceShell.css";

function WorkspaceShell({
  workspaceId,
  entityLabel,
  section,
  onSelectSection,
  onHandoff,
  quickActions,
  children,
  title,
  entityOptions,
  entityType,
  hiddenNav,
}) {
  const ctx = useActiveWorkspace();
  const workspace = getWorkspace(workspaceId) || (ctx && ctx.workspace);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const caps = (ctx && ctx.caps) || null;
  const crumbs = buildBreadcrumbs(
    workspace,
    entityLabel || (ctx && ctx.entityLabel),
    section && String(section).indexOf("soon:") === 0 ? null : section
  );

  const pageTitle =
    title ||
    (workspace
      ? workspace.displayName +
        (entityLabel ? " · " + entityLabel : "")
      : "Workspace");

  const switchWorkspace = (id) => {
    if (!ctx || !ctx.activate) return;
    const ws = getWorkspace(id);
    ctx.activate(id, null, null);
    writeStoredWorkspace(id, null, null);
    if (!ws) return;
    if (ws.autoHandoff && ws.bladeHandoff) {
      startWorkspaceHandoff(ws);
      return;
    }
    if (typeof window !== "undefined") {
      window.location.assign(ws.route);
    }
  };

  const entities = Array.isArray(entityOptions) ? entityOptions : [];
  const activeEntity =
    (ctx && ctx.entityId) ||
    (typeof window !== "undefined" &&
      window.location.pathname.split("/").pop()) ||
    null;

  return (
    <div className="ws-shell">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <aside
        className={
          "ws-shell__sidebar" + (sidebarOpen ? " is-open" : "")
        }
      >
        <div className="ws-shell__brand">
          <p className="ws-shell__brand-label">Workspace</p>
          <h1 className="ws-shell__brand-title">
            {workspace ? (
              <span className="ws-shell__title-row">
                <WorkspaceIcon name={workspace.icon} />
                {workspace.displayName}
              </span>
            ) : (
              "Workspace"
            )}
          </h1>
          {entities.length > 1 ? (
            <label className="ws-shell__entity-picker">
              <span className="ws-muted">Entity</span>
              <select
                value={activeEntity || ""}
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id || !entityType) return;
                  writeStoredWorkspace(workspaceId, id, null);
                  window.location.assign(
                    "/workspace/" +
                      (entityType === "organization"
                        ? "organization"
                        : entityType === "project"
                          ? "project"
                          : "creator") +
                      "/" +
                      id
                  );
                }}
              >
                {entities.map((id) => (
                  <option key={id} value={id}>
                    {entityLabel && String(id) === String(activeEntity)
                      ? entityLabel
                      : "#" + id}
                  </option>
                ))}
              </select>
            </label>
          ) : entityLabel ? (
            <p className="ws-shell__entity">{entityLabel}</p>
          ) : null}
        </div>
        <WorkspaceNav
          workspace={workspace}
          caps={caps}
          hiddenIds={hiddenNav}
          section={section}
          onSelectSection={(s) => {
            setSidebarOpen(false);
            if (onSelectSection) onSelectSection(s);
          }}
          onHandoff={onHandoff}
        />
        <div className="ws-shell__switcher">
          <p className="ws-shell__switcher-label">Switch workspace</p>
          {(ctx && ctx.available ? ctx.available : []).map((w) => (
            <button
              key={w.id}
              type="button"
              className={w.id === workspaceId ? "is-active" : ""}
              onClick={() => switchWorkspace(w.id)}
            >
              <WorkspaceIcon name={w.icon} />
              <span>{w.displayName}</span>
            </button>
          ))}
        </div>
      </aside>
      <div className="ws-shell__main">
        <header className="ws-shell__header">
          <div>
            <button
              type="button"
              className="ws-shell__menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              Menu
            </button>
            <nav className="ws-shell__crumbs" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <span key={i}>
                  {i > 0 ? " / " : null}
                  {c.to ? <Link to={c.to}>{c.label}</Link> : c.label}
                </span>
              ))}
            </nav>
          </div>
          <div className="ws-shell__actions">
            <Link className="ws-btn ws-btn--ghost" to="/search">
              Search
            </Link>
            <Link className="ws-btn ws-btn--ghost" to="/notification/view-all">
              Notifications
            </Link>
            {(quickActions || []).map((a) =>
              a.href ? (
                <Link key={a.label} className="ws-btn ws-btn--ghost" to={a.href}>
                  {a.label}
                </Link>
              ) : a.external ? (
                <a
                  key={a.label}
                  className="ws-btn ws-btn--ghost"
                  href={a.external}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {a.label}
                </a>
              ) : (
                <button
                  key={a.label}
                  type="button"
                  className="ws-btn ws-btn--ghost"
                  onClick={a.onClick}
                >
                  {a.label}
                </button>
              )
            )}
            <Link className="ws-btn" to="/account">
              Account
            </Link>
          </div>
        </header>
        <div className="ws-shell__content">{children}</div>
      </div>
    </div>
  );
}

export default WorkspaceShell;
